import { useState } from "react";
import { Check, X, Ban, Plus, Edit2, CheckCircle2, Lock } from "lucide-react";
import type { AdminUser } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { admins as adminsApi } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";
import { Dialog, FieldLabel, dialogInput } from "../../components/ui/Dialog";

// ── Admin users ───────────────────────────────────────────────────────────────

export function AdminUsersView({ addToast }: { addToast: AddToast }) {
  const { ADMIN_USERS, refetchAdmins } = useAdminData();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Moderator");
  const [inviteName, setInviteName] = useState("");
  const [creating, setCreating] = useState(false);
  // A temporary password is shown once, in a dialog it can be copied from (a browser alert() cannot).
  const [secret, setSecret] = useState<{ heading: string; email: string; password: string; note: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null);
  const [roleChoice, setRoleChoice] = useState("");
  const ROLES = ["Moderator", "Senior Moderator", "Finance", "Support", "Admin"];

  async function copySecret() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret.password);
    } catch {
      // Clipboard access can be blocked (plain http, permissions): fall back to a selection copy.
      const el = document.getElementById("secret-password") as HTMLInputElement | null;
      el?.select();
      document.execCommand("copy");
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // There is no invitation email service behind this panel, so the account is
  // created directly with a generated password that is shown once, here, for
  // the admin to hand over. (A fake "invite sent" toast used to be all this did.)
  function generatePassword(): string {
    const bytes = new Uint32Array(3);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(36)).join("-");
  }

  function createAdmin() {
    if (!inviteEmail) return;
    const password = generatePassword();
    setCreating(true);
    adminsApi.create({
      name: inviteName || inviteEmail.split("@")[0],
      email: inviteEmail,
      password,
      role: inviteRole,
    })
      .then(() => {
        refetchAdmins();
        setInviteOpen(false);
        setInviteEmail(""); setInviteRole("Moderator"); setInviteName("");
        setSecret({
          heading: "Account created",
          email: inviteEmail,
          password,
          note: "This is shown once. Send it to them over a secure channel and have them change it.",
        });
        addToast("Admin account created", "success");
      })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to create admin", "error"))
      .finally(() => setCreating(false));
  }

  // Hands over an account that can suspend users and move escrow, so the
  // backend restricts this to a Super Admin.
  function resetPassword(u: AdminUser) {
    const password = generatePassword();
    adminsApi.update(u.id, { password })
      .then(() => {
        setResetTarget(null);
        setSecret({
          heading: "Password reset",
          email: u.email,
          password,
          note: "This is shown once. Send it over a secure channel; they can change it from the sidebar.",
        });
        addToast("Password reset", "success");
      })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to reset password", "error"));
  }

  function saveRole() {
    if (!roleTarget || !roleChoice) return;
    const u = roleTarget;
    adminsApi.update(u.id, { role: roleChoice })
      .then(() => { refetchAdmins(); setRoleTarget(null); addToast(`${u.name} is now ${roleChoice}`, "success"); })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to update role", "error"));
  }

  return (
    <div>
      <PageHeader title="Admin users" sub="Manage admin access and roles" actions={
        <Btn variant="primary" onClick={() => setInviteOpen(true)} iconOnlyPhone><Plus size={13} /> Add admin</Btn>
      } />

      {secret && (
        <Dialog title={secret.heading} onClose={() => { setSecret(null); setCopied(false); }}>
          <p style={{ fontSize: "12px", color: T.sub, margin: "0 0 10px" }}>Temporary password for <strong>{secret.email}</strong></p>
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <input id="secret-password" readOnly value={secret.password} onFocus={(e) => e.currentTarget.select()} style={{ ...dialogInput, fontFamily: "'Space Mono', monospace" }} />
            <Btn variant="primary" onClick={() => void copySecret()}>{copied ? "Copied" : "Copy"}</Btn>
          </div>
          <p style={{ fontSize: "12px", color: T.sub, lineHeight: 1.6, margin: "0 0 18px" }}>{secret.note}</p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setSecret(null); setCopied(false); }}>Done</Btn>
          </div>
        </Dialog>
      )}

      {resetTarget && (
        <Dialog title={`Reset ${resetTarget.name}'s password?`} onClose={() => setResetTarget(null)}>
          <p style={{ fontSize: "13px", color: T.sub, lineHeight: 1.6, margin: "0 0 20px" }}>Their current password stops working immediately and you hand them a new one.</p>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setResetTarget(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={() => resetPassword(resetTarget)}>Reset password</Btn>
          </div>
        </Dialog>
      )}

      {roleTarget && (
        <Dialog title={`Edit role for ${roleTarget.name}`} onClose={() => setRoleTarget(null)}>
          <div style={{ marginBottom: "18px" }}>
            <FieldLabel>Role</FieldLabel>
            <select value={roleChoice} onChange={(e) => setRoleChoice(e.target.value)} style={dialogInput}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setRoleTarget(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={saveRole} disabled={roleChoice === roleTarget.role}>Save role</Btn>
          </div>
        </Dialog>
      )}

      {/* Invite modal */}
      {inviteOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "16px", padding: "28px", width: "420px", maxWidth: "90vw", maxHeight: "90dvh", overflowY: "auto", fontFamily: F }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 700, color: T.text, margin: 0 }}>Add admin user</h3>
              <button onClick={() => setInviteOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: "4px" }}><X size={16} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Email address *</p>
                <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="name@donatella.sa" type="email"
                  style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: F, outline: "none", color: T.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Full name (optional)</p>
                <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="e.g. Rania Mohammed"
                  style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: F, outline: "none", color: T.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Role</p>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {["Moderator", "Senior Moderator", "Finance", "Support", "Admin"].map((r) => (
                    <button key={r} onClick={() => setInviteRole(r)}
                      style={{ padding: "5px 13px", borderRadius: "9999px", fontSize: "11px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${inviteRole === r ? T.text : T.border}`, background: inviteRole === r ? T.text : T.surface, color: inviteRole === r ? "#fff" : T.sub, transition: "all 0.12s" }}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ background: T.s2, borderRadius: "8px", padding: "10px 14px" }}>
                <p style={{ fontSize: "11px", color: T.sub, fontFamily: F, lineHeight: 1.6, margin: 0 }}>
                  The account is created immediately with a generated temporary password, shown to you once. Send it over a secure channel and have them change it.
                </p>
              </div>
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <Btn variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Btn>
                <Btn variant="primary" onClick={createAdmin} disabled={!inviteEmail || creating}><Check size={13} /> Create account</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px),1fr))", gap: "14px", marginBottom: "24px" }}>
        {ADMIN_USERS.map((u) => (
          <Card key={u.id} style={{ padding: "18px 20px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.role === "Super Admin" ? CORAL : T.text, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#fff", fontFamily: F }}>{u.initials}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>{u.name}</p>
                  <StatusBadge status={u.status} />
                </div>
                <p style={{ fontSize: "11px", color: CORAL, fontFamily: F, fontWeight: 600, marginTop: "2px" }}>{u.role}</p>
                <p style={{ fontSize: "10px", color: T.sub, fontFamily: F, marginTop: "2px" }}>{u.email}</p>
                <p style={{ fontSize: "10px", color: T.muted, fontFamily: F, marginTop: "4px" }}>Last login: {u.lastLogin}</p>
              </div>
            </div>
            {u.role !== "Super Admin" && (
              <div style={{ marginTop: "12px", display: "flex", gap: "6px" }}>
                <Btn variant="ghost" size="xs" onClick={() => { setRoleTarget(u); setRoleChoice(ROLES.includes(u.role) ? u.role : ROLES[0]); }}><Edit2 size={10} /> Edit role</Btn>
                <Btn variant="ghost" size="xs" onClick={() => setResetTarget(u)}><Lock size={10} /> Reset password</Btn>
                <Btn variant="destructive" size="xs" onClick={() => adminsApi.remove(u.id).then(() => { refetchAdmins(); addToast(`${u.name} deactivated`, "error"); }).catch(() => addToast("Failed to deactivate", "error"))}><Ban size={10} /></Btn>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Card style={{ padding: "20px" }}>
        <SectionLabel>Role permissions overview</SectionLabel>
        <TableWrap>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F, minWidth: "600px" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Role", "Verify", "Moderate", "Finance", "Manage users", "Export", "Announce", "Settings"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { role: "Super Admin", v: true, m: true, f: true, u: true, e: true, a: true, s: true },
                { role: "Admin", v: true, m: true, f: false, u: false, e: true, a: true, s: false },
                { role: "Senior Moderator", v: true, m: true, f: false, u: false, e: false, a: false, s: false },
                { role: "Moderator", v: true, m: true, f: false, u: false, e: false, a: false, s: false },
                { role: "Finance", v: false, m: false, f: true, u: false, e: true, a: false, s: false },
                { role: "Support", v: false, m: false, f: false, u: false, e: false, a: false, s: false },
              ].map((r, i, arr) => (
                <tr key={r.role} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <td style={{ padding: "10px 12px", fontSize: "12px", fontWeight: 600, color: T.text }}>{r.role}</td>
                  {[r.v, r.m, r.f, r.u, r.e, r.a, r.s].map((perm, pi) => (
                    <td key={pi} style={{ padding: "10px 12px" }}>
                      {perm ? <CheckCircle2 size={14} style={{ color: "#166534" }} /> : <X size={14} style={{ color: T.muted }} />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>
    </div>
  );
}
